import React from "react";
import { Box, Grid, Link, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { palette } from "../palette";
import { fadeUp, revealProps, stagger, useReducedMotion } from "../motion";
import { Section } from "./Section";
import { Spot } from "../illustrations/Spot";

// Real work shipped out of the program and our own projects/research track. Add
// entries as they land.
const PROJECTS = [
  {
    name: "Reverie",
    tagline: "Bedtime stories & sleep relaxation",
    url: "https://www.reverieapp.in/",
    icon: "launch",
    body: "Turns screen time into sleep time. A hands-free bedtime audio app that dims and locks the screen once a story begins, spanning six story worlds alongside sound baths, Yoga Nidra and NSDR, all controllable by voice. Free on iOS and Android.",
  },
  {
    name: "AI Book Writer",
    tagline: "Open-source library that writes books on any topic",
    url: "https://github.com/edgentai/ai-book-writer",
    icon: "content",
    body: "Turns a pile of source material into a finished book. A three-stage Python pipeline parses PDFs, images and video, builds a table of contents by clustering themes and reading prerequisites off a knowledge graph, then has writer, student and reviewer agents draft each chapter against the sources. Open source on GitHub.",
  },
  {
    name: "AI Educational Video Generation",
    tagline: "Multi-agent system for explainer videos",
    // TODO: repo link to follow — see `url` handling below for the no-link state.
    icon: "roadmap",
    status: "In progress",
    body: "A multi-agent system that takes a topic and produces a finished educational video — agents plan the lesson, draft the script, generate the visuals and narration, then assemble and review the result.",
  },
];

export const Projects = () => {
  const reduced = useReducedMotion();

  return (
    <Section
      id="student-work"
      eyebrow="Projects and research"
      title="Things being built here"
      lead="Work coming out of the program and our own projects and research track — some shipped, some still being built."
    >
      <motion.div variants={stagger(0.08)} {...revealProps(reduced)}>
        <Grid container spacing={3} justifyContent="center">
          {PROJECTS.map((p) => (
            <Grid item xs={12} md={PROJECTS.length >= 3 ? 4 : PROJECTS.length === 2 ? 6 : 7} key={p.name}>
              <motion.div variants={fadeUp}>
                <Box sx={{ borderTop: `1px solid ${palette.border}`, pt: 3, height: "100%" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                    <Spot kind={p.icon} size={38} />
                    <Box>
                      {/* Links only when there is somewhere to go; an entry
                          without a url renders as plain text plus its status. */}
                      <Typography
                        component="h3"
                        sx={{
                          fontWeight: 700,
                          fontSize: 19,
                          display: "flex",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: 1,
                          color: p.url ? palette.primary : palette.text,
                        }}
                      >
                        {p.url ? (
                          <Link
                            href={p.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="none"
                            sx={{
                              color: palette.primary,
                              "&:hover": { textDecoration: "underline" },
                            }}
                          >
                            {p.name}
                          </Link>
                        ) : (
                          p.name
                        )}
                        {p.status && (
                          <Box
                            component="span"
                            sx={{
                              fontSize: 11.5,
                              fontWeight: 600,
                              letterSpacing: "0.04em",
                              textTransform: "uppercase",
                              color: palette.textFaint,
                              backgroundColor: palette.bgAlt,
                              borderRadius: "999px",
                              px: 1.2,
                              py: 0.35,
                            }}
                          >
                            {p.status}
                          </Box>
                        )}
                      </Typography>
                      <Typography
                        component="p"
                        sx={{ color: palette.textMuted, fontSize: 14.5 }}
                      >
                        {p.tagline}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography
                    component="p"
                    sx={{ color: palette.textMuted, fontSize: 15, lineHeight: 1.62 }}
                  >
                    {p.body}
                  </Typography>
                </Box>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </motion.div>
    </Section>
  );
};

export default Projects;
